<template>
  <div class="flex flex-col items-center gap-[12px] w-full">
    <div class="pb-[12px] w-full">
      <h2 class="text-[28px] max-md:text-[24px] font-bold text-[#3a3d44] text-center leading-[1.3]">
        Создать аккаунт
      </h2>
    </div>

    <!-- Ошибка: почта уже зарегистрирована (Screens 3 & 7) -->
    <p v-if="emailExists" class="text-[#f8465b] text-base text-center">
      Такая почта уже зарегистрирована!
    </p>

    <form class="flex flex-col items-center gap-[12px] w-full" @submit.prevent="handleSubmit">
      <!-- Email -->
      <UINewInput
        v-model="emailValue"
        placeholder="Email"
        type="email"
        class="!w-[368px] max-md:!w-full"
        input-classes="!rounded-[12px] !border-[#d9e5ff] w-full"
        :disabled="showPasswordFields"
        :validation-rules="[
          { type: 'required', message: 'Введите email' },
          { type: 'email', message: 'Некорректный email' },
        ]"
        @update:validation-error="emailError = $event"
      >
        <template v-if="showPasswordFields" #postfix>
          <div class="flex items-center justify-center p-1 cursor-pointer" @click="editEmail">
            <i class="fi fi-sr-pencil text-base flex text-primary/60 hover:text-primary" />
          </div>
        </template>
      </UINewInput>

      <!-- Пароль (Screens 6 & 7) -->
      <UINewInput
        v-if="showPasswordFields || showLoginPassword"
        v-model="password"
        placeholder="Введите пароль"
        :type="showPassword ? 'text' : 'password'"
        class="!w-[368px] max-md:!w-full"
        input-classes="!rounded-[12px] !border-[#d9e5ff] w-full"
        :validation-rules="[
          { type: 'required', message: 'Введите пароль' },
          ...(showPasswordFields ? [{ type: 'minLength', value: 6, message: 'Пароль должен содержать более 6-ти символов' }] : []),
        ]"
        @update:validation-error="passwordError = $event"
      >
        <template #postfix>
          <div class="flex items-center justify-center p-1 cursor-pointer" @click="showPassword = !showPassword">
            <i class="fi fi-sr-eye text-xl flex" :class="showPassword ? 'text-primary' : 'text-primary/40'" />
          </div>
        </template>
      </UINewInput>

      <!-- Повторите пароль (только Screen 6) -->
      <UINewInput
        v-if="showPasswordFields"
        v-model="passwordConfirm"
        placeholder="Повторите пароль"
        :type="showPasswordConfirm ? 'text' : 'password'"
        class="!w-[368px] max-md:!w-full"
        input-classes="!rounded-[12px] !border-[#d9e5ff] w-full"
        :error-message="passwordMismatchError"
      >
        <template #postfix>
          <div class="flex items-center justify-center p-1 cursor-pointer" @click="showPasswordConfirm = !showPasswordConfirm">
            <i class="fi fi-sr-eye text-xl flex" :class="showPasswordConfirm ? 'text-primary' : 'text-primary/40'" />
          </div>
        </template>
      </UINewInput>

      <p v-if="serverError && !passwordError" class="text-[#f8465b] text-sm w-[368px] max-md:w-full">{{ serverError }}</p>

      <!-- Кнопка -->
      <button
        type="submit"
        class="bg-primary rounded-[12px] py-3 w-[368px] max-md:w-full text-white font-bold text-base disabled:opacity-50"
        :disabled="submitDisabled"
      >
        <span v-if="loading" class="loader-inline mr-2" />
        {{ buttonText }}
      </button>
    </form>

    <!-- Назад -->
    <div class="flex items-center justify-center">
      <span class="text-primary text-sm cursor-pointer" @click="handleBack">Назад</span>
    </div>

    <!-- Забыли пароль? Восстановить (Screens 6 & 7) -->
    <div v-if="showPasswordFields || showLoginPassword" class="flex items-center justify-center gap-1.5 text-sm text-[#3A3D44]/60">
      Забыли пароль?
      <span class="text-primary cursor-pointer" @click="$emit('setMode', LoginMode.RECOVERY_EMAIL)">
        Восстановить
      </span>
    </div>

    <!-- Юридический текст (Screens 6 & 7) -->
    <p
      v-if="showPasswordFields || showLoginPassword"
      class="font-normal text-xs text-center text-[rgba(58,61,68,0.6)] pt-[12px] w-[350px] max-md:w-full max-md:px-4"
    >
      Продолжая, вы соглашаетесь с
      <a href="/pdf/oferta.pdf" target="_blank" class="underline !text-[rgba(58,61,68,0.6)] !font-normal">офертой</a>
      и даёте
      <a href="/pdf/consent.pdf" target="_blank" class="underline !text-[rgba(58,61,68,0.6)] !font-normal">согласие</a>
      на обработку данных в соответствии с
      <a href="/pdf/politic.pdf" target="_blank" class="underline !text-[rgba(58,61,68,0.6)] !font-normal">политикой в отношении обработки персональных данных</a>
    </p>
  </div>
</template>

<script setup lang="ts">
import { LoginMode, LoginScreenContext } from '~/interfaces/login/UI';
import { ApiKey } from '~/common/API/baseApi';
import { useUserStore } from '~/store/UserModule';
import AuthService from '~/services/AuthService';

const emit = defineEmits<{
  (e: 'setMode', mode: LoginMode): void;
  (e: 'setEmail', email: string): void;
  (e: 'setRegistrationId', id: number): void;
  (e: 'setExpiredAt', expiredAt: string): void;
  (e: 'setPassword', password: string): void;
  (e: 'authorized'): void;
}>();

const props = defineProps<{
  email: string;
  context: LoginScreenContext;
}>();

// Внутренние состояния:
// 'email' = Screen 5 (только email + "Далее")
// 'password' = Screen 6 (email + пароль + повтор + "Создать")
// 'emailExists' = Screen 3 (ошибка + email + "Войти")
// 'emailExistsLogin' = Screen 7 (ошибка + email + пароль + "Войти")
type Step = 'email' | 'password' | 'emailExists' | 'emailExistsLogin';
const step = ref<Step>('email');

const emailValue = ref(props.email);
const emailError = ref('');
const password = ref('');
const passwordConfirm = ref('');
const passwordError = ref('');
const serverError = ref('');
const loading = ref(false);
const showPassword = ref(false);
const showPasswordConfirm = ref(false);

const emailExists = computed(() => step.value === 'emailExists' || step.value === 'emailExistsLogin');
const showPasswordFields = computed(() => step.value === 'password');
const showLoginPassword = computed(() => step.value === 'emailExistsLogin');

watch(emailValue, () => {
  if (step.value === 'emailExists' || step.value === 'emailExistsLogin') {
    step.value = 'email';
    serverError.value = '';
  }
});

watch(password, () => {
  serverError.value = '';
});

const passwordMismatchError = computed(() => {
  if (!passwordConfirm.value) return '';
  return password.value !== passwordConfirm.value ? 'Пароли не совпадают' : '';
});

const buttonText = computed(() => {
  switch (step.value) {
    case 'email': return 'Далее';
    case 'password': return 'Создать';
    case 'emailExists':
    case 'emailExistsLogin':
      return 'Войти';
  }
});

const submitDisabled = computed(() => {
  if (loading.value || !!emailError.value || !emailValue.value) return true;
  if (step.value === 'password') {
    return !!passwordError.value || !password.value || password.value !== passwordConfirm.value || !passwordConfirm.value;
  }
  if (step.value === 'emailExistsLogin') {
    return !!passwordError.value || !password.value;
  }
  return false;
});

const editEmail = () => {
  step.value = 'email';
  password.value = '';
  passwordConfirm.value = '';
  serverError.value = '';
};

const handleBack = () => {
  if (step.value === 'password' || step.value === 'emailExists' || step.value === 'emailExistsLogin') {
    // Назад к вводу email
    step.value = 'email';
    password.value = '';
    passwordConfirm.value = '';
    serverError.value = '';
  } else {
    // Назад к выбору (Screen 1)
    emit('setMode', LoginMode.EMAIL_CHOICE);
  }
};

const handleSubmit = async () => {
  if (submitDisabled.value) return;

  loading.value = true;
  serverError.value = '';

  // Screen 5: проверяем email
  if (step.value === 'email') {
    try {
      const { available } = await AuthService.checkEmail(emailValue.value);
      emit('setEmail', emailValue.value);
      if (available) {
        // Email свободен → Screen 6 (пароль)
        step.value = 'password';
      } else {
        // Email занят → Screen 3 (ошибка)
        step.value = 'emailExists';
      }
    } catch {
      serverError.value = 'Не удалось проверить email. Попробуйте позже.';
    } finally {
      loading.value = false;
    }
    return;
  }

  // Screen 6: регистрация
  if (step.value === 'password') {
    try {
      const { id } = await AuthService.registerStart(emailValue.value);
      const { expiredAt } = await AuthService.registerStep2(id, password.value);
      emit('setEmail', emailValue.value);
      emit('setRegistrationId', id);
      emit('setPassword', password.value);
      if (expiredAt) emit('setExpiredAt', expiredAt);
      emit('setMode', LoginMode.CONFIRM_CODE);
    } catch {
      serverError.value = 'Не удалось завершить регистрацию. Попробуйте позже.';
    } finally {
      loading.value = false;
    }
    return;
  }

  // Screen 3: клик "Войти" → показать пароль (Screen 7)
  if (step.value === 'emailExists') {
    step.value = 'emailExistsLogin';
    password.value = '';
    passwordError.value = '';
    serverError.value = '';
    loading.value = false;
    return;
  }

  // Screen 7: логин
  if (step.value === 'emailExistsLogin') {
    try {
      const { id, accessToken } = await AuthService.loginEmail(emailValue.value, password.value);
      ApiKey.set(accessToken);
      useCookie("authorizedP").value = accessToken;
      const userStore = useUserStore();
      userStore.setAuthorized('', accessToken, String(id));
      userStore.loginDialogVisible = false;
      if (props.context === LoginScreenContext.null) {
        window.location.reload();
      } else {
        emit('authorized');
      }
    } catch {
      serverError.value = 'Неверный пароль!';
    } finally {
      loading.value = false;
    }
  }
};
</script>
